/**
 * The price, in one place.
 * @file src/web/shared/pricing.ts
 *
 * ── Why this is not written twice ─────────────────────────────────────────
 * The landing page and /pricing both quote the price. Written twice they
 * drift, and the drift is not cosmetic here: a firm that reads $29 on the
 * home page and $39 on the pricing page has caught the company being careless
 * with the one number it is asking to be trusted about.
 *
 * ── The model ─────────────────────────────────────────────────────────────
 * $29 per user, per month. One price, every feature, no volume tiers and no
 * cap on how much intake a firm runs through it.
 *
 * This replaced flat tiered pricing ($49 / $149 / $349 by intake volume with
 * unlimited seats). The old model's whole argument was that the bill did not
 * move when a firm hired, and that argument is now retired — every claim that
 * rested on it had to go with it, not be left standing next to a per-user
 * price. A site that says "no per-seat billing" beside a per-seat price is
 * not making an argument, it is making a mistake.
 *
 * The comparison narrowed with it, to Clio Grow alone — the one product doing
 * the same job at the same unit. See SEAT_MATH for why the practice
 * management suites came off the table rather than being quoted as prices we
 * beat.
 *
 * ── The two rules that carried over ───────────────────────────────────────
 *   · No feature is held back. Drop-off detection, 33 languages, scoring,
 *     e-signature and conflict checking are in the price. Withholding any of
 *     them from a smaller firm would sell a worse product to the firm least
 *     able to notice it was worse.
 *   · No cap on intake. The price scales with the people using it, not with
 *     how many enquiries a firm takes — a form that gets busy should never be
 *     a reason to be charged more mid-month.
 *
 * @llm-rule WHEN: Quoting a price anywhere in the product or on the site
 * @llm-rule AVOID: Reintroducing tiers, or gating a feature behind a price
 * @llm-rule AVOID: Any claim that the bill does not move with headcount — it
 *   does now, and the site must not say otherwise
 */

/**
 * The same two figures as numbers, for the places that do arithmetic.
 *
 * The pricing calculator and the partner-share calculator both multiply the
 * price out. Parsing '$29' back into 29 at each call site is how one of them
 * ends up multiplying a stale literal, so the number is exported once and the
 * strings above are what gets printed.
 */
export const PRICE_USD = 29;
export const ANNUAL_PRICE_USD = 296;

/** What a firm pays, per user, per month. */
export const PRICE = {
  amount: '$29',
  per: 'per user, per month',
  /** Said on its own, where a sentence needs the whole thing. */
  full: '$29 per user, per month',
  /** What is included, which is all of it. */
  includes: 'Every feature, unlimited intake, unlimited e-signatures.',
};

/**
 * The annual discount.
 *
 * Monthly is the default on the page: it is what a firm trying a new tool
 * actually wants, and leading with the annual figure to look cheaper is the
 * trick this pricing page exists to avoid. Annual is a choice, one click
 * away.
 *
 * $29 × 12 = $348, less 15% = $295.80, printed as $296 because a price with
 * eighty cents in it reads as a number chosen to look smaller.
 */
export const ANNUAL_SAVING = '15%';
export const ANNUAL_PRICE = {
  amount: '$296',
  per: 'per user, per year',
  /** The same thing monthly, for a reader doing the division anyway. */
  monthlyEquivalent: '$24.67 a month',
};

/**
 * What the one comparable product charges.
 *
 * ── Why this is Clio Grow alone ───────────────────────────────────────────
 * This table used to carry MyCase Pro beside it at $100–130, and that was a
 * dishonest column. MyCase is a practice management suite — billing,
 * documents, client portal, case management — with intake as one part of a
 * much larger whole. Our own comparison page says so in as many words, and
 * then the pricing page put the two prices side by side as though they
 * bought the same thing. A reader who knows the market spots that in a
 * second and stops believing the rest of the page.
 *
 * It is wrong on product terms too: Naumene runs ALONGSIDE the case
 * system. MyCase is something we integrate with, not something we are a
 * cheaper version of, and quoting its suite price as a saving argues against
 * our own integration.
 *
 * Clio Grow stays because it is the same job: the intake and CRM layer that
 * sits in front of a case system, billed per user, which is now our unit
 * too. Same work, same unit, one subtraction.
 *
 * ── This is arithmetic, not a claim ───────────────────────────────────────
 * Published list price times the number of users, both columns, so a reader
 * can redo it. Dated, because a competitor's price is a fact with a shelf
 * life, and caveated in the open: vendors discount at volume, and saying so
 * costs nothing.
 */
export const SEAT_MATH = {
  competitor: { vendor: 'Clio Grow', rate: '$59', note: 'per user, per month' },
  ours: { label: 'Naumene', rate: '$29', note: 'per user, per month' },
  /*
   * The three-person row is kept even though it is the least impressive
   * number: a table that only showed the ten-person case would be
   * cherry-picked, and the modest figure is what makes the larger ones
   * believable.
   */
  rows: [
    { seats: '3 people', ours: '$87', theirs: '$177', save: '$90' },
    { seats: '5 people', ours: '$145', theirs: '$295', save: '$150' },
    { seats: '10 people', ours: '$290', theirs: '$590', save: '$300' },
  ],
  headline: { seats: 'ten', monthly: '$300', yearly: '$3,600' },
  /** Dated, because a competitor's price is a fact with a shelf life. */
  asOf: 'September 2026',
  /** Said on the page, not buried here. */
  notCompared:
    'Only Clio Grow is on this table. MyCase, Smokeball and the other practice management suites cost more per user because they are a great deal more software — case management, billing, documents. Naumene runs alongside one of those rather than replacing it, so putting its price beside theirs would be comparing a part to a whole.',
};

/**
 * The charges that are not on the bill.
 *
 * Two entries were removed when the model changed rather than reworded: "No
 * per-seat billing" and "No charge for the people who only look" were both
 * true of flat pricing and are both false now. Leaving either in place, even
 * softened, would have been the site arguing against its own price list.
 *
 * "No cap on intake" came off later for a different reason: it is true, but
 * it is already the second item in the panel's included list further up the
 * page. This section is for charges a reader would not otherwise know were
 * absent, not a second pass over what the price buys.
 */
export const NOT_CHARGED = [
  {
    title: 'No onboarding fee',
    detail:
      'Clio Grow charges around $399 one-time to get started. Here setup is part of the subscription.',
  },
  {
    title: 'No AI surcharge',
    detail:
      'Clio charges $25 for every matter its Grow AI helps sign — twenty signed matters is another $500 that month. Here the AI is part of the subscription.',
  },
  {
    title: 'No cap on signatures',
    detail:
      'Clio’s lower plans allow 3 and 15 e-signatures a month; Lead Docket and Lawmatics have no e-signature at all. Unlimited here.',
  },
  {
    title: 'No feature held back',
    detail:
      'Drop-off detection, 33 languages, scoring, e-signature and conflict checking are in the price. There is one plan, so there is nothing to upgrade to.',
  },
];
